/**
 * `<BrandingProvider>` — Story 5.6.
 *
 * Owns per-event branding state and is the SINGLE writer of:
 *   - `<style id="pvt-branding-<eventId>">` element appended to `<head>`
 *     containing two `:root[data-event="<eventId>"][data-theme="dark|light"]`
 *     rules with the four `--brand-*` custom properties (architecture
 *     §2.3 lines 208-215).
 *   - `<html data-event="<eventId>">` attribute that pairs with Story
 *     5.2's `<html data-theme>` attribute via the CSS attribute
 *     combinator selector. Both attributes coexist on `<html>` and
 *     combine: `<html data-event="A" data-theme="dark">` activates the
 *     `:root[data-event="A"][data-theme="dark"]` rule, which has higher
 *     specificity than Story 5.1's `:root[data-theme="dark"]` baseline,
 *     so the per-event `--brand-*` overrides win the cascade.
 *
 * Lifecycle (LOCKED, story spec lines 48-52 + Critical Scope Decisions
 * #2 + #3):
 *
 *   1. On mount with valid `branding`: pre-remove any stale
 *      `<style id="pvt-branding-<eventId>">` (StrictMode double-mount
 *      idempotency per CSD #2), append a fresh `<style>` element with
 *      the two-rule CSS string, set `<html data-event="<eventId>">`.
 *   2. On `branding` prop change from one event to another: cleanup
 *      the previous event's `<style>` + `<html data-event>`, then
 *      mount the new event's block.
 *   3. On `branding` prop change to `null`: cleanup; no new write.
 *      Children render against the neutral default theme tokens
 *      (FR32.3 fallback path).
 *   4. On unmount: cleanup. The `useEffect` cleanup function removes
 *      the `<style>` element and the `<html data-event>` attribute.
 *
 * Validation contract (Critical Scope Decision #3 — fallback-on-invalid-
 * input):
 *
 *   - `eventId` MUST match `/^[0-9A-HJKMNP-TV-Z]{26}$/` (ULID Crockford-
 *     base32 alphabet per https://github.com/ulid/spec — excludes I, L,
 *     O, U).
 *   - Each color hex MUST match `/^#[0-9A-Fa-f]{6}$/`.
 *   - On validation failure: emit `console.warn`, mount NO `<style>`
 *     block, set NO `<html data-event>`, render children with the
 *     neutral default. Same UX as `branding === null` path. The
 *     load-bearing assumption is that `branding` values come from the
 *     server-validated event API (Stories 6.3 / 6.4 / 8.1 / 9.1 /
 *     12.x); this validation is defense-in-depth.
 *   - Non-HTTPS `logoUrl` (CSD #4): emit `console.warn` BUT still
 *     mount the `<style>` block. Brand colors are independent of the
 *     logo URL; rejecting the entire branding because of one bad URL
 *     would over-fail.
 *
 * Forward references (story spec lines 56 + 144-160):
 *   - Story 6.1 (admin shell): first per-route composer; wraps
 *     event-detail pages in `<BrandingProvider>`.
 *   - Story 6.5 (event-create wizard): consumes `deriveBrandTokens`
 *     output; wraps preview pane.
 *   - Stories 8.1 / 8.4 (submitter landing + UI): consumes
 *     `GET /e/{eventId}/submit/landing`. NOTE: Story 8.1 AC line 1601
 *     currently lists nested-short-name field shape; CSD #9 + #20 flag
 *     the discrepancy in deferred-work.md for resolution at Story 8.1
 *     implementation.
 *   - Stories 9.1 / 9.7 (voter landing + UI): consumes
 *     `GET /e/{eventId}/vote/landing`.
 *   - Stories 12.2 / 12.4 / 12.7 (broadcast surfaces): each consumes
 *     a public broadcast endpoint and feeds `<BrandingProvider>`.
 *   - Story 7.1+ (admin event-detail): consumes
 *     `useBranding()?.logoUrl` to render the event logo.
 */
import { useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { BrandingContext } from './BrandingContext';
import type { Branding, BrandingContextValue } from './BrandingContext';

/**
 * ULID Crockford-base32 character set: 0-9, A-H, J, K, M, N, P-T, V-Z
 * (excludes I, L, O, U for ambiguity-avoidance per
 * https://github.com/ulid/spec). Matches what the server-side ULID
 * generator produces (Story 6.4 + architecture §2.3).
 */
const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/;

/**
 * 6-digit hex color (`#RRGGBB`). Matches what Story 6.3's
 * `deriveBrandTokens` produces (architecture §2.4 line 235) and what
 * Story 5.3's `meetsAA` accepts.
 */
const HEX_6_RE = /^#[0-9A-Fa-f]{6}$/;

export interface BrandingProviderProps {
  /**
   * Per-event branding tokens, or `null` for non-event scopes (admin
   * home, public placeholder). When `null`, no overrides are mounted
   * and children render against neutral defaults.
   */
  branding: Branding | null;
  children: ReactNode;
}

/**
 * Validates a `Branding` object against the locked regex contracts.
 * Returns the offending field name (string) on first failure, or
 * `null` if all fields are valid. Caller emits `console.warn` and
 * falls back to the no-branding render path on non-null return.
 */
function validateBranding(b: Branding): string | null {
  if (!ULID_RE.test(b.eventId)) return 'eventId';
  if (!HEX_6_RE.test(b.brandPrimaryDark)) return 'brandPrimaryDark';
  if (!HEX_6_RE.test(b.brandPrimaryLight)) return 'brandPrimaryLight';
  if (!HEX_6_RE.test(b.brandOnPrimaryDark)) return 'brandOnPrimaryDark';
  if (!HEX_6_RE.test(b.brandOnPrimaryLight)) return 'brandOnPrimaryLight';
  if (!HEX_6_RE.test(b.brandSoft)) return 'brandSoft';
  if (!HEX_6_RE.test(b.brandStrong)) return 'brandStrong';
  return null;
}

/**
 * Builds the two-rule CSS string to inject into the per-event
 * `<style>` element. Format matches architecture §2.3 lines 208-215
 * verbatim. Values flow directly into the `<style>` text content
 * because they are server-validated (regex-asserted above) before
 * reaching this function. CSS.escape is NOT used (it's for selectors;
 * irrelevant for hex property values).
 */
function buildCssText(b: Branding): string {
  return [
    `:root[data-event="${b.eventId}"][data-theme="dark"] {`,
    `  --brand-primary: ${b.brandPrimaryDark};`,
    `  --brand-on-primary: ${b.brandOnPrimaryDark};`,
    `  --brand-soft: ${b.brandSoft};`,
    `  --brand-strong: ${b.brandStrong};`,
    `}`,
    `:root[data-event="${b.eventId}"][data-theme="light"] {`,
    `  --brand-primary: ${b.brandPrimaryLight};`,
    `  --brand-on-primary: ${b.brandOnPrimaryLight};`,
    `  --brand-soft: ${b.brandSoft};`,
    `  --brand-strong: ${b.brandStrong};`,
    `}`,
  ].join('\n');
}

/**
 * Computes the predictable `<style>` element id for a given eventId.
 * The deterministic id is load-bearing for StrictMode double-mount
 * idempotency (CSD #2): the mount effect always pre-removes any
 * existing element with this id before appending, so two consecutive
 * mounts (StrictMode dev behavior) never produce two `<style>` blocks.
 */
function styleIdFor(eventId: string): string {
  return `pvt-branding-${eventId}`;
}

/**
 * Removes any `<style id="pvt-branding-<eventId>">` from `<head>`.
 * Idempotent: returns silently if the element doesn't exist.
 */
function removeStyleElement(eventId: string): void {
  if (typeof document === 'undefined') return;
  const existing = document.getElementById(styleIdFor(eventId));
  if (existing && existing.parentNode) {
    existing.parentNode.removeChild(existing);
  }
}

/**
 * Removes `<html data-event>` if and only if its current value matches
 * the given eventId. This guard prevents a cleanup function from
 * clobbering an attribute that a later mount has already set (a real
 * concern when prop change goes A → B: the A cleanup runs AFTER the B
 * mount has set `data-event="B"`).
 */
function clearDataEventIfMatching(eventId: string): void {
  if (typeof document === 'undefined') return;
  if (document.documentElement.getAttribute('data-event') === eventId) {
    document.documentElement.removeAttribute('data-event');
  }
}

/**
 * Validates `logoUrl` (when non-null). Defense-in-depth per CSD #4:
 * emits `console.warn` for non-HTTPS URLs but does NOT cause the
 * provider to skip mounting. Returns true when the URL is acceptable
 * (HTTPS or null), false when it triggered a warning.
 */
function isHttpsLogoUrl(logoUrl: string | null): boolean {
  if (logoUrl === null) return true;
  return logoUrl.startsWith('https://');
}

export function BrandingProvider({
  branding,
  children,
}: BrandingProviderProps) {
  // Determine whether the active branding is renderable. Validation
  // failures reduce to the "no branding" path (children render with
  // neutral defaults) per CSD #3.
  const validationField = branding ? validateBranding(branding) : null;
  const renderableBranding =
    branding && validationField === null ? branding : null;

  useEffect(() => {
    if (!branding) {
      // No branding active. Effect is a no-op; the prior cleanup (if
      // any) handles teardown of a previous event's overrides.
      return;
    }

    // Validation gate. Warn once per render of an invalid value, but
    // skip the DOM writes. CSD #3 — fallback-on-invalid-input.
    if (validationField !== null) {
      // eslint-disable-next-line no-console
      console.warn(
        `BrandingProvider: invalid \`branding.${validationField}\` — falling back to no-branding render. ` +
          `Expected ULID for eventId or 6-digit hex (\`#RRGGBB\`) for color fields.`,
      );
      return;
    }

    // Defense-in-depth logoUrl warn (CSD #4). Does NOT skip the mount;
    // brand colors mount as normal.
    if (!isHttpsLogoUrl(branding.logoUrl)) {
      // eslint-disable-next-line no-console
      console.warn(
        `BrandingProvider: \`branding.logoUrl\` is not HTTPS (got "${branding.logoUrl}"). ` +
          `Mounting brand colors anyway; consumer logo render will fail per browser HTTPS-blocking.`,
      );
    }

    if (typeof document === 'undefined') return;

    const eventId = branding.eventId;
    const styleId = styleIdFor(eventId);

    // Pre-remove for StrictMode idempotency (CSD #2). Even outside
    // StrictMode, this handles the rare case where a previous unmount
    // failed to clean up (e.g., browser tab freeze). The deterministic
    // id makes the pre-remove safe.
    removeStyleElement(eventId);

    // Build + append the fresh <style> block.
    const styleElement = document.createElement('style');
    styleElement.id = styleId;
    styleElement.textContent = buildCssText(branding);
    document.head.appendChild(styleElement);

    // Set <html data-event="<eventId>">. The attribute pairs with
    // Story 5.2's <html data-theme> via the CSS attribute combinator.
    document.documentElement.setAttribute('data-event', eventId);

    return () => {
      // Cleanup on unmount, prop change, or branding=null transition.
      // Always remove the style element; it's keyed by THIS effect's
      // eventId (the closure captured the value at effect-creation time).
      removeStyleElement(eventId);
      // Clear data-event ONLY if it still matches our eventId. If a
      // later effect has already swapped to a different eventId, the
      // guard prevents us from clobbering its setAttribute.
      clearDataEventIfMatching(eventId);
    };
  }, [branding, validationField]);

  // Stabilize the context value object so consumers reading `useBranding()`
  // don't re-render on every parent render. Mirrors Story 5.2's
  // `ThemeProvider` `useMemo` pattern (Code Review Medium 2026-05-16):
  // without the memo, the literal-object expression produces a new
  // reference each render, busting React's `Object.is` reference equality
  // check on context value and forcing all `useBranding()` consumers to
  // re-render even when `branding` field values are unchanged.
  //
  // Provider value derives from `renderableBranding` — a validation failure
  // reduces the visible context to the null state even though the consumer
  // passed a non-null `branding` prop. This keeps the component-tree
  // behavior consistent with the DOM-side fallback (no overrides mounted
  // ↔ no eventId exposed).
  const value = useMemo<BrandingContextValue>(
    () => ({
      branding: renderableBranding,
      eventId: renderableBranding?.eventId ?? null,
      logoUrl: renderableBranding?.logoUrl ?? null,
    }),
    [renderableBranding],
  );

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}
